import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Room } from '../models/Room';
import { Fire } from '../services/FireClient';
import { My } from '../services/My';
import { LocalStorage } from '../services/Utils/LocalStorage';
import { PushService } from '../services/Utils/PushService';
import { UtilApp } from '../services/Utils/UtilApp';

/**
 * 앱 초기 네비게이션을 처리하는 훅
 * - /main 경로에서 적절한 방으로 리다이렉트
 * - 푸시 알림으로 온 경우 해당 방으로 이동
 * - URL 파라미터로 room_id가 있으면 해당 방으로 이동
 * 
 * 이 훅은 LeftMenuRooms가 렌더링되지 않아도 호출되어야 하므로
 * App.tsx 레벨에서 사용합니다.
 */
export function useInitialNavigation(
    onRoomsLoaded?: (rooms: Room[]) => void
) {
    const navigate = useNavigate();
    const location = useLocation();
    const hasNavigatedRef = useRef(false);
    const unsubscribeRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        // 이미 네비게이션 처리가 완료된 경우 스킵
        if (hasNavigatedRef.current) return;

        // 방 목록 구독 및 초기 네비게이션 처리
        unsubscribeRef.current = Fire.subscribeToMyRooms(My.user.id, (roomList) => {
            My.setRooms(roomList);
            onRoomsLoaded?.(roomList);

            // 아직 네비게이션 처리 안했으면 처리
            if (!hasNavigatedRef.current) {
                handleInitialNavigation(roomList);
            }

            // NOTE: 개인 방(Personal room) 생성은 Cloud Functions의 onCreateUserTrigger에서 처리됨
            // 클라이언트에서 자동 생성하면 삭제 후 재생성되는 문제가 발생함
        });

        // App state 변경 리스너 (푸시 알림에서 앱 열기)
        let appStateListenerHandle: { remove: () => Promise<void> } | null = null;

        App.addListener('appStateChange', (state) => {
            const lastPushRoomId = LocalStorage.get("last-push-room");
            console.log('App state changed. Is active?', state.isActive, lastPushRoomId);
            if (state.isActive && lastPushRoomId) {
                LocalStorage.set("last-push-room", '');
                navigate('/room/' + lastPushRoomId, { replace: true });
            }
        }).then(handle => {
            appStateListenerHandle = handle;
        });

        return () => {
            unsubscribeRef.current?.();
            appStateListenerHandle?.remove();
        };
    }, []);

    function handleInitialNavigation(rooms: Room[]) {
        hasNavigatedRef.current = true;

        const openRoomId = UtilApp.getParam('room_id');
        const lastPushRoomId = LocalStorage.get("last-push-room");
        const currentPath = window.location.pathname;

        if (openRoomId) {
            // URL 파라미터로 room_id가 있으면 해당 방으로 이동
            navigate('/room/' + openRoomId, { replace: true });
        } else if (lastPushRoomId) {
            // 푸시 알림으로 온 경우 해당 방으로 이동
            LocalStorage.set("last-push-room", '');
            navigate('/room/' + lastPushRoomId, { replace: true });
            PushService.GoToRoomId = null;
        } else if (currentPath === '/main' || currentPath === '/') {
            // /main 또는 / 경로인 경우
            const lastVisitedRoomId = LocalStorage.get("last-visited-room");

            if (rooms.length > 0) {
                if (lastVisitedRoomId && rooms.find(r => r.id === lastVisitedRoomId)) {
                    // 마지막 방문한 방이 있고, 해당 방이 목록에 있으면
                    navigate('/room/' + lastVisitedRoomId, { replace: true });
                } else {
                    // 첫 번째 방으로 이동
                    navigate('/room/' + rooms[0].id, { replace: true });
                }
            } else {
                // 방이 없으면 welcome 페이지로
                navigate('/welcome', { replace: true });
            }
        }
        // 그 외의 경로는 그대로 유지
    }

    return { hasNavigated: hasNavigatedRef.current };
}
